<template>
  <div class="feedback-container" v-if="showButton">
    <button class="feedback-trigger" @click="isOpen = true" v-if="!isOpen">
      Feedback
    </button>

    <div v-if="isOpen" class="feedback-modal-overlay" @click.self="isOpen = false">
      <div class="feedback-modal">
        <div class="modal-header">
          <h2>Dein Feedback</h2>
          <button class="close-btn" @click="isOpen = false">
            <img src="@/assets/images/cross.svg" alt="Schließen" />
          </button>
        </div>
        
        <p class="modal-description">
          Wie gefällt dir gefundn.? Wir freuen uns über dein Feedback, um die App stetig zu verbessern.
        </p>

        <textarea 
          v-model="feedbackContent" 
          placeholder="Schreibe uns deine Meinung..."
          :disabled="isSending"
          rows="5"
        ></textarea>

        <div class="modal-footer">
          <p v-if="error" class="error-msg">{{ error }}</p>
          <p v-if="success" class="success-msg">Danke für dein Feedback!</p>
          <button 
            class="submit-btn" 
            @click="sendFeedback" 
            :disabled="isSending || !feedbackContent.trim() || success"
          >
            <span v-if="!isSending && !success">Senden</span>
            <span v-else-if="isSending">Wird gesendet...</span>
            <span v-else-if="success">Gesendet ✓</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import AuthService from '@/services/AuthService';

const route = useRoute();
const isOpen = ref(false);
const feedbackContent = ref('');
const isSending = ref(false);
const success = ref(false);
const error = ref('');

const showButton = computed(() => {
  return route.path !== '/account' && route.path !== '/chat' && route.path !== '/settings';
});

const sendFeedback = async () => {
  if (!feedbackContent.value.trim()) return;

  isSending.value = true;
  error.value = '';
  
  try {
    const res = await AuthService.submitFeedback({
      content: feedbackContent.value
    });

    if (res.success) {
      success.value = true;
      feedbackContent.value = '';
      setTimeout(() => {
        isOpen.value = false;
        success.value = false;
      }, 2000);
    } else {
      error.value = res.msg || 'Ein Fehler ist aufgetreten.';
    }
  } catch (err) {
    console.error(err);
    error.value = 'Feedback konnte nicht gesendet werden.';
  } finally {
    isSending.value = false;
  }
};
</script>

<style scoped>
.feedback-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.feedback-trigger {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.4rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.feedback-trigger:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.feedback-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  animation: fadeIn 0.3s ease;
}

.feedback-modal {
  background: white;
  width: 90%;
  max-width: 400px;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn img {
  width: 20px;
  height: 20px;
  filter: contrast(0.5);
}

.modal-description {
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.4;
}

textarea {
  width: 100%;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 12px;
  font-family: inherit;
  font-size: 1rem;
  resize: none;
  outline: none;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

textarea:focus {
  border-color: var(--primary-color, #EE6300);
}

.modal-footer {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.submit-btn {
  width: 100%;
  background: var(--primary-color, #EE6300);
  color: white;
  border: none;
  padding: 12px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.submit-btn:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(238, 99, 0, 0.3);
}

.error-msg {
  color: #ff4d4f;
  font-size: 0.85rem;
  margin: 0;
}

.success-msg {
  color: #52c41a;
  font-size: 0.85rem;
  margin: 0;
  font-weight: 600;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
</style>
